// src/admin/PrintCards.js
import React, { useState, useRef, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import Card from './Card'; // Assuming Card.js is in admin folder or adjust path
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import './PrintCards.css'; // Create this file if it doesn't exist
// Ensure you have necessary CSS for .background-option, .background-option-selected
// You might need to import parts of AdminManagement.css or create a new CSS file

const PrintCards = () => {
  const { allStudentsForOutlet } = useOutletContext(); // Receive filtered students
  // const { classMap } = useContext(StudentContext); // If needed for anything specific here

  const [selectedCards, setSelectedCards] = useState([]);
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState('bg-1');
  const [customBackground, setCustomBackground] = useState(null);
  const cardRefs = useRef({});

  const allStudents = allStudentsForOutlet || []; // Use data passed from parent

  const handleSelectCard = (studentId) => {
    setSelectedCards((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCards(allStudents.map((student) => student.id));
    } else {
      setSelectedCards([]);
    }
  };

  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error(`Không thể tải hình ảnh: ${src}. Error: ${err}`));
    });
  };

  const downloadCard = async (studentId) => {
    const cardElement = cardRefs.current[studentId];
    if (!cardElement) return;
    try {
      const qrCodeImg = cardElement.querySelector('.qr-code-image');
      const logoImg = cardElement.querySelector('.logo');
      if (qrCodeImg && qrCodeImg.src) await preloadImage(qrCodeImg.src);
      if (logoImg && logoImg.src) await preloadImage(logoImg.src);
      const canvas = await html2canvas(cardElement, { scale: 10, useCORS: true });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `card_${studentId}.png`;
      link.click();
    } catch (error) {
      console.error('Lỗi khi tải thẻ:', error);
      alert(`Có lỗi xảy ra khi tải thẻ: ${error.message}. Vui lòng kiểm tra console.`);
    }
  };

  const downloadSelectedCards = async () => {
    if (selectedCards.length === 0) return;
    const zip = new JSZip();
    for (const studentId of selectedCards) {
      const cardElement = cardRefs.current[studentId];
      if (cardElement) {
        try {
          const qrCodeImg = cardElement.querySelector('.qr-code-image');
          const logoImg = cardElement.querySelector('.logo');
          if (qrCodeImg && qrCodeImg.src) await preloadImage(qrCodeImg.src);
          if (logoImg && logoImg.src) await preloadImage(logoImg.src);
          const canvas = await html2canvas(cardElement, { scale: 10, useCORS: true });
          const imgData = canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
          zip.file(`card_${studentId}.png`, imgData, { base64: true });
        } catch (error) {
          console.error(`Lỗi khi xử lý thẻ ${studentId}:`, error);
          alert(`Có lỗi khi xử lý thẻ ${studentId}: ${error.message}.`);
        }
      }
    }
    if (Object.keys(zip.files).length > 0) {
        zip.generateAsync({ type: 'blob' }).then((content) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = 'student_cards.zip';
        link.click();
        });
    } else {
        alert("Không có thẻ nào được xử lý để tải về.");
    }
  };

  const handleBackgroundSelect = (bgType) => {
    setSelectedBackground(bgType);
    setCustomBackground(null);
    setBackgroundModalOpen(false);
  };

  const handleCustomBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCustomBackground(reader.result);
        setSelectedBackground('custom');
        setBackgroundModalOpen(false);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">In Thẻ</h2>
      <div className="flex flex-wrap justify-center items-center mb-4 gap-4">
        <button
          onClick={() => setBackgroundModalOpen(true)}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Chọn Hình Nền
        </button>
        <button
          id="download-selected-btn"
          onClick={downloadSelectedCards}
          disabled={selectedCards.length === 0}
          className={`px-6 py-3 rounded ${selectedCards.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
        >
          Tải về các thẻ đã chọn ({selectedCards.length})
        </button>
      </div>

      {backgroundModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Chọn Hình Nền</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <button
                onClick={() => handleBackgroundSelect('bg-1')}
                className={`p-2 border rounded h-20 bg-gray-200 background-option bg-1 ${selectedBackground === 'bg-1' ? 'ring-2 ring-blue-500' : ''}`}
              >
                Hình Nền 1
              </button>
              <button
                onClick={() => handleBackgroundSelect('bg-2')}
                className={`p-2 border rounded h-20 bg-gray-300 background-option bg-2 ${selectedBackground === 'bg-2' ? 'ring-2 ring-blue-500' : ''}`}
              >
                Hình Nền 2
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tải lên hình nền tùy chỉnh:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleCustomBackgroundUpload}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setBackgroundModalOpen(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table id="card-table" className="min-w-full w-full">
          <thead>
            <tr>
              <th className="p-2 w-12">
                <input
                  type="checkbox"
                  id="select-all-checkbox"
                  title="Chọn/Bỏ chọn tất cả"
                  onChange={handleSelectAll}
                  checked={allStudents.length > 0 && selectedCards.length === allStudents.length}
                  disabled={allStudents.length === 0}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
              </th>
              <th className="p-2 text-left">Thẻ Thiếu Nhi</th>
              <th className="p-2 w-32 text-center">Tải về</th>
            </tr>
          </thead>
          <tbody id="card-table-body">
            {allStudents.map((student) => (
              <Card // Make sure Card component is correctly imported and styled
                key={student.id}
                student={student}
                selectedBackground={selectedBackground}
                customBackground={customBackground}
                cardRefs={cardRefs}
                handleSelectCard={handleSelectCard}
                selectedCards={selectedCards}
                downloadCard={downloadCard}
              />
            ))}
            {allStudents.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center p-4 border-t">
                  Không có học sinh nào trong lớp được chọn, hoặc không có dữ liệu học sinh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PrintCards;